import React, { useEffect, useState } from "react";
import { useAuth } from "../../lib/auth";
import { Link } from "react-router-dom";
import { CreditCard, Crown, X, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const PLANS = [
  { id: "free", name: "Free", price: 0 },
  { id: "premium", name: "Premium", price: 19 },
  { id: "luxury", name: "Luxury", price: 49 },
  { id: "pro", name: "Planner Pro", price: 99 },
];

function KeepPhotosCard() {
  const { user } = useAuth();
  if (!user || user.plan === "pro" || user.role !== "couple") return null;
  if (user.memories_kept_forever) return <p className="mb-6 text-sm bg-[#C8A96B]/15 rounded-xl px-4 py-3" data-testid="keep-photos-active">Your photos are kept for the life of your account.</p>;
  async function buy() {
    try { const { data } = await api.post("/payments/checkout", { plan_id: "memory_keep", origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className="mb-8 rounded-2xl bg-[var(--ink)] text-white p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between" data-testid="keep-photos-card">
      <div><p className="font-serif text-2xl">Keep your photos forever</p><p className="text-sm text-white/65 mt-1">Not going Pro? Add this before your wedding ends and every photo and memory stays up for the life of your account.</p></div>
      <button onClick={buy} className="btn-gold shrink-0" data-testid="keep-photos-buy">Keep them · $9.99 once</button>
    </div>
  );
}

export default function BillingPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    const { data } = await api.get("/billing/me");
    setData(data);
  }
  useEffect(() => { load().catch(() => {}); }, []);

  async function changePlan(plan_id) {
    if (plan_id === data?.plan) return;
    if (plan_id === "free") {
      if (!confirm("Cancel your subscription and downgrade to Free?")) return;
      setBusy(true);
      try {
        await api.post("/billing/cancel");
        toast.success("Subscription cancelled. You're now on Free.");
        load();
      } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
      return;
    }
    setBusy(true);
    try {
      const { data: r } = await api.post("/payments/checkout", { plan_id, origin_url: window.location.origin });
      window.location.href = r.url;
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }

  if (!data) return null;
  const current = PLANS.find(p => p.id === data.plan) || PLANS[0];

  return (
    <div data-testid="billing-page">
      <p className="font-script text-[var(--gold)] text-3xl">Billing</p>
      <h1 className="font-serif font-light text-4xl mb-8">Subscription & payments</h1>
      <KeepPhotosCard />

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-[var(--ink)] text-white rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-2 mb-3"><Crown className="w-5 h-5 text-[var(--gold)]" /><p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">Current plan</p></div>
          <p className="font-serif text-5xl mb-1">{current.name}</p>
          <p className="text-white/60 text-sm">{current.price === 0 ? "+ $9.99 one-time setup" : `$${current.price}/month`}</p>
          {data.free_months_credited > 0 && <p className="text-xs text-[var(--gold)] mt-3">{data.free_months_credited} free month(s) credited from referrals</p>}
        </div>
        <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-3">Affiliate balance</p>
          <p className="font-serif text-4xl">{data.points_balance} <span className="text-base text-[var(--muted)] font-body">pts</span></p>
          <Link to="/dashboard/referrals" className="text-sm text-[var(--gold)] mt-3 inline-flex items-center gap-1">Earn more <ArrowUpRight className="w-3 h-3" /></Link>
        </div>
      </div>

      <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8 mb-8">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-4">Change plan</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {PLANS.map((p) => (
            <button key={p.id} data-testid={`change-${p.id}`} onClick={() => changePlan(p.id)} disabled={busy || p.id === data.plan} className={`text-left p-5 rounded-xl border transition ${p.id === data.plan ? "border-[var(--gold)] bg-[var(--bg-warm)]" : "border-[var(--line)] hover:border-[var(--gold)]"} disabled:cursor-not-allowed`}>
              <p className="font-serif text-xl">{p.name}</p>
              <p className="text-sm text-[var(--muted)]">{p.price === 0 ? "Free" : `$${p.price}/mo`}</p>
              {p.id === data.plan && <p className="text-xs text-[var(--gold)] mt-2 font-medium">Current</p>}
              {p.id !== data.plan && p.id === "free" && <p className="text-xs text-red-600 mt-2">Cancel & downgrade</p>}
              {p.id !== data.plan && p.id !== "free" && <p className="text-xs text-[var(--gold)] mt-2">Switch →</p>}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-4">Recent transactions</p>
        {data.transactions?.length === 0 ? <p className="text-sm text-[var(--muted)] py-6 text-center">No transactions yet.</p> : (
          <div className="space-y-2">
            {data.transactions.map((t) => (
              <div key={t.session_id} className="flex items-center justify-between py-3 border-b border-[var(--line)] last:border-0">
                <div>
                  <p className="text-sm font-medium">{t.plan_id} {t.promo_code ? <span className="text-xs text-[var(--gold)]">· {t.promo_code}</span> : ""}</p>
                  <p className="text-xs text-[var(--muted)]">{new Date(t.created_at).toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium">${(t.amount || 0).toFixed(2)} <span className="text-xs text-[var(--muted)]">{t.currency?.toUpperCase()}</span></p>
                  <p className={`text-xs ${t.payment_status === "paid" ? "text-[var(--gold)]" : "text-[var(--muted)]"}`}>{t.payment_status}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
